import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  type StepIconProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { ISkuQualificationRequest } from "services/KnowfullyAdminClient";
import {
  emptyLinkState,
  hasAnchor,
  type LinkState,
  type ReferenceRow,
} from "./qualificationLinkState";
import {
  emptyWizardFormValues,
  toSkuQualificationRequests,
  overridesAreValid,
  withShownOverrides,
  type WizardFormValues,
} from "./wizardSave";
import Step1SelectQualification from "./Step1SelectQualification";
import Step2ConfigureDetails from "./Step2ConfigureDetails";

type WizardProps = {
  open: boolean;
  onClose: () => void;
  referenceRows: ReferenceRow[];
  loadingRows?: boolean;
  skuCredits?: number;
  onSave: (
    requests: ISkuQualificationRequest[],
  ) => Promise<{ [key: string]: string } | undefined>;
};

// Step icon: filled dark circle carrying the step number, no check mark when completed.
const STEP_DARK = "#37474f";

const styles = {
  stepIcon: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 500,
  },
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    pr: 1,
  },
  stepperBar: { px: 3, pb: 1 },
  loading: { display: "flex", justifyContent: "center", p: 4 },
};

function WizardStepIcon({ active, completed, icon }: StepIconProps) {
  const filled = active || completed;
  return (
    <Box sx={{ ...styles.stepIcon, bgcolor: filled ? STEP_DARK : "grey.400" }}>
      {icon}
    </Box>
  );
}

export default function QualificationWizardDialog({
  open,
  onClose,
  referenceRows,
  loadingRows = false,
  skuCredits,
  onSave,
}: WizardProps) {
  const { formatMessage: t } = useIntl();
  const [step, setStep] = useState(0);
  const [linkState, setLinkState] = useState<LinkState>(emptyLinkState());

  const entries = linkState.links;

  const handleClose = () => {
    setStep(0);
    setLinkState(emptyLinkState());
    onClose();
  };

  const handleSubmit = (values: WizardFormValues) =>
    onSave(toSkuQualificationRequests(entries, withShownOverrides(values)));

  const steps = [
    t({ id: "skuQualificationWizardStepSelect" }),
    t({ id: "skuQualificationWizardStepConfigure" }),
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box sx={styles.titleBar}>
        <DialogTitle id="qualification-wizard-title">
          {t({ id: "skuQualificationAddTitle" })}
        </DialogTitle>
        <IconButton onClick={handleClose} aria-label={t({ id: "closeButton" })}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.stepperBar}>
        <Stepper activeStep={step} sx={{ maxWidth: 420 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={WizardStepIcon}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Form<WizardFormValues>
        onSubmit={handleSubmit}
        initialValues={emptyWizardFormValues()}
        render={({ handleSubmit: submit, submitting, submitError, values }) => {
          const canNext = hasAnchor(linkState) && entries.length > 0;
          const canSave =
            entries.length > 0 &&
            (values.professionIds?.length ?? 0) > 0 &&
            overridesAreValid(values.creditLines || undefined);

          return (
            <form onSubmit={submit} noValidate>
              <DialogContent dividers>
                {submitError && <Alert severity="error">{submitError}</Alert>}

                <Box sx={{ display: step === 0 ? "block" : "none" }}>
                  {loadingRows ? (
                    <Box sx={styles.loading}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Step1SelectQualification
                      referenceRows={referenceRows}
                      state={linkState}
                      onChange={setLinkState}
                    />
                  )}
                </Box>

                {step === 1 && <Step2ConfigureDetails entries={entries} skuCredits={skuCredits} />}
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose}>
                  {t({ id: "cancelButton" })}
                </Button>
                <Box sx={{ flex: 1 }} />
                {step === 1 && (
                  <Button onClick={() => setStep(0)}>
                    {t({ id: "backLabel" })}
                  </Button>
                )}
                {step === 0 && (
                  <Button
                    variant="contained"
                    disabled={!canNext}
                    onClick={() => setStep(1)}
                  >
                    {t({ id: "nextButton" })}
                  </Button>
                )}
                {step === 1 && (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!canSave || submitting}
                    startIcon={submitting && <CircularProgress size={16} />}
                  >
                    {t({ id: "saveButton" })}
                  </Button>
                )}
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
}
